import type {
  Action,
  Alert,
  AlertContext,
  Difficulty,
  GameConfig,
  GameState,
  PlayerDecision,
} from './types';
import { DIFFICULTY_CONFIG } from './types';
import {
  ALERT_TEMPLATES,
  ATTACK_CHAINS,
  createSeededRandom,
  type SeededRandom,
} from './templates';
import { calculateStats, evaluateDecision } from './scoring';

let alertCounter = 0;
let chainCounter = 0;

function formatTimestamp(date: Date): string {
  return date.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

function generateAlert(
  rng: SeededRandom,
  difficulty: Difficulty,
  templateId?: string,
  contextOverrides?: Partial<AlertContext>,
  timestampOverride?: Date
): Alert {
  const template = templateId
    ? (ALERT_TEMPLATES.find((t) => t.id === templateId) ?? rng.pick(ALERT_TEMPLATES))
    : rng.pick(ALERT_TEMPLATES);
  const generated = template.generate(rng, difficulty, contextOverrides);
  alertCounter++;

  const timestamp = timestampOverride ? new Date(timestampOverride) : new Date();
  if (!timestampOverride) {
    timestamp.setMinutes(timestamp.getMinutes() - rng.nextInt(1, 60));
  }

  return {
    id: `alert-${alertCounter}-${rng.nextInt(1000, 9999)}`,
    title: template.title,
    category: template.category,
    timestamp: formatTimestamp(timestamp),
    severity: generated.severity ?? 'MEDIUM',
    summary: generated.summary ?? '',
    details: generated.details ?? {},
    context: {
      ...(generated.context ?? {
        source: {
          ip: '0.0.0.0',
          type: 'internal',
          isKnownBad: false,
          isKnownGood: false,
        },
        target: {
          hostname: 'unknown',
          ip: '0.0.0.0',
          role: 'web',
          criticality: 'medium',
          isPublic: false,
        },
        relatedAlerts: [],
      }),
      source: contextOverrides?.source ??
        generated.context?.source ?? {
          ip: '0.0.0.0',
          type: 'internal',
          isKnownBad: false,
          isKnownGood: false,
        },
      target: contextOverrides?.target ??
        generated.context?.target ?? {
          hostname: 'unknown',
          ip: '0.0.0.0',
          role: 'web',
          criticality: 'medium',
          isPublic: false,
        },
      relatedAlerts: generated.context?.relatedAlerts ?? [],
    },
    correctAction: generated.correctAction,
    explanation: generated.explanation ?? '',
    mitreTechnique: generated.mitreTechnique,
  };
}

function generateAttackChain(rng: SeededRandom, difficulty: Difficulty): Alert[] {
  chainCounter++;
  const chain = rng.pick(ATTACK_CHAINS);
  const chainId = `${chain.id}-${chainCounter}`;

  // Force all chain alerts to be true positives (escalate)
  const chainAlerts: Alert[] = [];
  const stepGapMinutes = rng.nextInt(3, 10);
  const endOffsetMinutes = rng.nextInt(5, 20);
  const chainStart = new Date();
  chainStart.setMinutes(
    chainStart.getMinutes() -
      endOffsetMinutes -
      stepGapMinutes * (chain.templateIds.length - 1)
  );
  let chainScope: AlertContext['chainScope'] | null = null;

  for (let i = 0; i < chain.templateIds.length; i++) {
    const template = ALERT_TEMPLATES.find((t) => t.id === chain.templateIds[i]);
    if (!template) continue;

    // Regenerate until we get a true positive for the chain
    let alert: Alert;
    let attempts = 0;
    const maxAttempts = 80;
    const stepTimestamp = new Date(chainStart);
    stepTimestamp.setMinutes(chainStart.getMinutes() + i * stepGapMinutes);
    do {
      alert = generateAlert(
        rng,
        difficulty,
        chain.templateIds[i],
        undefined,
        stepTimestamp
      );
      attempts++;
    } while (alert.correctAction !== 'escalate' && attempts < maxAttempts);

    // If we couldn't get an escalate, force it for the chain
    if (alert.correctAction !== 'escalate') {
      alert.correctAction = 'escalate';
      if (alert.severity === 'LOW') {
        alert.severity = 'MEDIUM';
      } else if (alert.severity === 'MEDIUM') {
        alert.severity = 'HIGH';
      }
      alert.summary = `Coordinated attack indicator: ${alert.summary}`;
      alert.explanation = `Part of coordinated attack chain: ${chain.name}. ${alert.explanation}`;
      alert.context.threatIntelNote = alert.context.threatIntelNote
        ? `Chain correlation: ${chain.name}. ${alert.context.threatIntelNote}`
        : `Chain correlation: ${chain.name}.`;
    }

    if (!chainScope) {
      chainScope = {
        source: { ...alert.context.source },
        target: { ...alert.context.target },
      };
    }

    alert.attackChainId = chainId;
    alert.attackChainStep = i + 1;
    alert.attackChainTotal = chain.templateIds.length;
    if (chainScope) {
      alert.context.chainScope = chainScope;
    }

    // Update explanation to mention it's part of a chain
    if (i > 0) {
      alert.context.relatedAlerts = [
        ...alert.context.relatedAlerts,
        {
          id: chainAlerts[i - 1].id,
          title: `[Chain] ${chainAlerts[i - 1].title}`,
          time: chainAlerts[i - 1].timestamp,
          severity: chainAlerts[i - 1].severity,
        },
      ];
    }

    chainAlerts.push(alert);
  }

  return chainAlerts;
}

function generateAlerts(config: GameConfig): Alert[] {
  const seed = config.seed ?? Date.now();
  const rng = createSeededRandom(seed);
  const alerts: Alert[] = [];

  // Ensure a mix of true positives and false positives
  // Difficulty affects the ratio
  const tpRatio =
    config.difficulty === 'intern'
      ? 0.5
      : config.difficulty === 'analyst'
        ? 0.35
        : config.difficulty === 'senior'
          ? 0.3
          : 0.2; // burnout mode has more noise

  // Determine if we should inject attack chains (not for intern)
  const chainCount =
    config.difficulty === 'intern'
      ? 0
      : config.difficulty === 'analyst'
        ? 1
        : config.difficulty === 'senior'
          ? 2
          : 3; // burnout gets more chains

  // Generate attack chains first
  const chainAlerts: Alert[] = [];
  for (let c = 0; c < chainCount; c++) {
    chainAlerts.push(...generateAttackChain(rng, config.difficulty));
  }

  // Calculate how many regular alerts we need
  const regularAlertCount = config.alertCount - chainAlerts.length;

  // Generate regular alerts
  for (let i = 0; i < regularAlertCount; i++) {
    let alert: Alert;
    let attempts = 0;

    // Try to maintain a reasonable TP/FP ratio (accounting for chain alerts which are all TPs)
    const totalTPs =
      alerts.filter((a) => a.correctAction === 'escalate').length + chainAlerts.length;
    const totalCount = alerts.length + chainAlerts.length;
    const currentRatio = totalCount > 0 ? totalTPs / totalCount : 0;
    const needMoreTPs = currentRatio < tpRatio - 0.1;
    const needMoreFPs = currentRatio > tpRatio + 0.1;

    do {
      alert = generateAlert(rng, config.difficulty);
      attempts++;

      // If we need to balance, regenerate if wrong type
      if (needMoreTPs && alert.correctAction !== 'escalate' && attempts < 10) {
        continue;
      }
      if (needMoreFPs && alert.correctAction === 'escalate' && attempts < 10) {
        continue;
      }
      break;
    } while (attempts < 10);

    alerts.push(alert);
  }

  // Interleave chain alerts with regular alerts
  const finalAlerts: Alert[] = [...alerts];

  // Spread chain alerts throughout the queue
  if (chainAlerts.length > 0) {
    // Group chain alerts by chain ID to keep them somewhat together
    const chainGroups: Map<string, Alert[]> = new Map();
    for (const alert of chainAlerts) {
      const group = chainGroups.get(alert.attackChainId!) || [];
      group.push(alert);
      chainGroups.set(alert.attackChainId!, group);
    }

    // Insert each chain's alerts sequentially, but spread chains out
    const chainGroupsArray = Array.from(chainGroups.values());
    for (let i = 0; i < chainGroupsArray.length; i++) {
      const group = chainGroupsArray[i];
      // Start position for this chain
      const startPosition = Math.floor(
        (i + 0.5) * (finalAlerts.length / (chainGroupsArray.length + 1))
      );

      for (let j = 0; j < group.length; j++) {
        // Insert with some random spacing (1-3 alerts between chain steps)
        const insertPosition = Math.min(
          startPosition + j * rng.nextInt(2, 4),
          finalAlerts.length
        );
        finalAlerts.splice(insertPosition, 0, group[j]);
      }
    }
  }

  return finalAlerts;
}

export function createGame(difficulty: Difficulty, seed?: number): GameState {
  const config: GameConfig = {
    ...DIFFICULTY_CONFIG[difficulty],
    seed: seed ?? Date.now(),
  };

  const alerts = generateAlerts(config);

  return {
    config,
    alerts,
    currentIndex: 0,
    decisions: [],
    results: [],
    stats: {
      totalAlerts: 0,
      correctCount: 0,
      incorrectCount: 0,
      totalPoints: 0,
      streak: 0,
      maxStreak: 0,
      precision: 1,
      recall: 1,
      avgTimeMs: 0,
      missedAttacks: 0,
      falseEscalations: 0,
    },
    startTime: Date.now(),
    isComplete: false,
  };
}

export function getCurrentAlert(state: GameState): Alert | null {
  if (state.isComplete || state.currentIndex >= state.alerts.length) {
    return null;
  }
  return state.alerts[state.currentIndex];
}

export function submitDecision(
  state: GameState,
  action: Action,
  timeSpent: number
): GameState {
  const alert = getCurrentAlert(state);
  if (!alert) return state;

  const decision: PlayerDecision = {
    alertId: alert.id,
    action,
    timeSpent,
    timestamp: Date.now(),
  };

  // Calculate current streak for scoring
  let currentStreak = 0;
  for (let i = state.results.length - 1; i >= 0; i--) {
    if (state.results[i].isCorrect) {
      currentStreak++;
    } else {
      break;
    }
  }

  const result = evaluateDecision(alert, decision, currentStreak);
  const newDecisions = [...state.decisions, decision];
  const newResults = [...state.results, result];
  const newIndex = state.currentIndex + 1;
  const isComplete = newIndex >= state.alerts.length;

  // Update streak
  const newStreak = result.isCorrect ? currentStreak + 1 : 0;
  const newMaxStreak = Math.max(state.stats.maxStreak, newStreak);

  // Recalculate full stats
  const newStats = calculateStats(state.alerts.slice(0, newIndex), newResults);
  newStats.streak = newStreak;
  newStats.maxStreak = newMaxStreak;

  return {
    ...state,
    currentIndex: newIndex,
    decisions: newDecisions,
    results: newResults,
    stats: newStats,
    isComplete,
  };
}

export function getProgress(state: GameState): {
  current: number;
  total: number;
  percentage: number;
} {
  return {
    current: state.currentIndex,
    total: state.alerts.length,
    percentage: Math.round((state.currentIndex / state.alerts.length) * 100),
  };
}

export function getTimeRemaining(
  state: GameState,
  alertStartTime: number
): number | null {
  if (state.config.timeLimit === 0) return null;
  const elapsed = (Date.now() - alertStartTime) / 1000;
  return Math.max(0, state.config.timeLimit - elapsed);
}

export { calculateStats, getGrade } from './scoring';
